import { Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { currentWorkDay, dateToDateOnly } from "@/lib/format";
import { useCreateRdo } from "../../hooks/use-create-rdo.ts";
import { existingRdoIdFromError, rdoErrorMessage } from "../../utils/errors.ts";

export function NewRdoDialog({ constructionId }: { constructionId: string }) {
	const { obra: obraKey } = useParams();
	const navigate = useNavigate();
	const createRdo = useCreateRdo(constructionId);
	const [open, setOpen] = useState(false);
	const [date, setDate] = useState<Date | undefined>(() => currentWorkDay());
	const [error, setError] = useState<string | null>(null);
	const [existingId, setExistingId] = useState<string | null>(null);

	const today = currentWorkDay();

	function handleOpenChange(next: boolean) {
		setOpen(next);
		if (next) {
			setDate(currentWorkDay());
			setError(null);
			setExistingId(null);
		}
	}

	function editorPath(rdoId: string) {
		return `/${obraKey}/rdo/${rdoId}`;
	}

	async function submit() {
		if (!date) return;
		setError(null);
		setExistingId(null);
		try {
			const rdo = await createRdo.mutateAsync(dateToDateOnly(date));
			setOpen(false);
			navigate(editorPath(rdo.id));
		} catch (err) {
			const existing = existingRdoIdFromError(err);
			if (existing) setExistingId(existing);
			setError(
				rdoErrorMessage(err, "Não foi possível criar o Relatório de Obra."),
			);
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button>
					<Plus /> Novo Relatório de Obra
				</Button>
			</DialogTrigger>
			<DialogContent
				className="new-rdo__dialog"
				onOpenAutoFocus={(event) => event.preventDefault()}
			>
				<DialogHeader>
					<DialogTitle>Novo Relatório de Obra</DialogTitle>
					<DialogDescription>
						O relatório é pré-preenchido com os relatos, o clima e as etapas do
						dia escolhido.
					</DialogDescription>
				</DialogHeader>
				<div className="new-rdo__field">
					<Label htmlFor="new-rdo-date">Data do relatório</Label>
					<DatePicker
						id="new-rdo-date"
						selected={date}
						onSelected={(next) => {
							setDate(next);
							setError(null);
							setExistingId(null);
						}}
						modal
						calendarProps={{ disabled: { after: today } }}
					/>
				</div>
				{error && (
					<div role="alert" className="new-rdo__error">
						<p>{error}</p>
						{existingId && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => {
									setOpen(false);
									navigate(editorPath(existingId));
								}}
							>
								Abrir Relatório de Obra existente
							</Button>
						)}
					</div>
				)}
				<DialogFooter>
					<Button
						type="button"
						variant="ghost"
						onClick={() => setOpen(false)}
						disabled={createRdo.isPending}
					>
						Cancelar
					</Button>
					<Button
						type="button"
						onClick={submit}
						disabled={!date || createRdo.isPending}
					>
						{createRdo.isPending && <Loader2 className="new-rdo__spinner" />}
						Criar Relatório de Obra
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
